import { Formik } from 'formik';
import EmailIcon from '@mui/icons-material/Email';
import TextField from '@mui/material/TextField';
import PropTypes from 'prop-types';
import React, {
  useCallback,
  useMemo,
  useState,
} from 'react';
import * as Yup from 'yup';

import CardText from 'components/Card/Text';
import CardTitle from 'components/Card/Title';
import LoadBlur from 'components/LoadBlur';
import auth from 'lib/auth';

import PrimaryButton from './PrimaryButton';
import SecondaryButton from './SecondaryButton';

const ForgotPassword = ({ loading, onEmail, onLoading, onLogin, onStateChange }) => {
  const [error, setError] = useState(null);
  return (
    <Formik
      initialValues={useMemo(
        () => ({
          email: '',
        }),
        [],
      )}
      onSubmit={useCallback(
        async ({ email }, { setSubmitting }) => {
          onEmail(email);
          onLoading(true);
          const { err, message, state } = await auth.forgotPassword(email);
          if (err) {
            setError(message);
            onLoading(false);
          } else if (state) {
            onStateChange(state);
          } else {
            onLoading(false);
          }
          setSubmitting(false);
        },
        [
          onEmail,
          onLoading,
          onStateChange,
        ],
      )}
      validationSchema={useMemo(
        () => Yup.object()
          .shape({
            email: Yup.string()
              .trim()
              .lowercase()
              .email('Email must be valid')
              .required('Email is required'),
          }),
        [],
      )}
    >
      {useCallback(
        ({
          dirty,
          errors,
          isSubmitting,
          isValid,
          setFieldTouched,
          setFieldValue,
          submitForm,
          touched,
          values,
        }) => {
          const busy = isSubmitting || loading;
          const canSubmit = !busy && dirty && isValid;
          return (
            <LoadBlur loading={isSubmitting}>
              <CardTitle title='Forgot your password?' />
              <CardText noTopPadding>
                To reset your password, please submit your email address
                below. If there is an account matching that email address, we
                will send instructions on how to proceed.
              </CardText>
              <CardText noTopPadding>
                <TextField
                  disabled={busy}
                  errorText={touched?.email && errors?.email}
                  label='Email'
                  fullWidth
                  onBlur={() => setFieldTouched('email')}
                  onChange={(e, value) => {
                    setError(null);
                    setFieldValue('email', value);
                  }}
                  value={values.email}
                />
              </CardText>
              <CardText noTopPadding>
                <SecondaryButton
                  label='Remember your password?'
                  onClick={onLogin}
                />
              </CardText>
              <PrimaryButton
                disabled={!canSubmit}
                error={error}
                fullWidth
                icon={<EmailIcon />}
                label='Send reset email'
                loading={busy}
                onClick={submitForm}
              />
            </LoadBlur>
          );
        },
        [
          error,
          loading,
          onLogin,
        ],
      )}
    </Formik>
  );
};

ForgotPassword.propTypes = {
  loading: PropTypes.bool,
  onEmail: PropTypes.func.isRequired,
  onLoading: PropTypes.func.isRequired,
  onLogin: PropTypes.func.isRequired,
  onStateChange: PropTypes.func.isRequired,
};

export default ForgotPassword;
